import { useWorkspace } from '@/features/workspaces/hooks';
import { DistributionChannelAdvancedParameter, DistributionChannelContent } from '@/models/distributionChannels';
import { useGetEstimateQuery } from '@/services/endpoints/audiences';
import { usePatchDistributionChannelMutation } from '@/services/endpoints/distributionChannels';
import { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import TargetIcon from '../../../../../../../assets/icons/target.svg?react';
import UsersIcon from '../../../../../../../assets/icons/users.svg?react';
import DestinationFieldIcon from '../../../../../../../assets/icons/destinationFields.svg?react';
import RefeshIcon from '../../../../../../../assets/icons/refresh.svg?react';
import { StepPageActions, StepPageHint } from '@/global/components';
import { BaseAudience } from '@/models/audiences';
import { CyclrTemplate } from '@/services/endpoints/integrations';
import { AudienceTemplateLinkMessage } from '@/features/audiences';
import { RecapItem } from '@/global/components/Recap';

type AudienceDestinationSyncProps = {
  distributionChannelContent: DistributionChannelContent;
  selectedAudience: BaseAudience;
  selectedTemplate: CyclrTemplate;
  onSyncSuccess: VoidFunction;
  prevStep: VoidFunction;
  onCancel: VoidFunction;
};

export const AudienceDestinationSync: FC<AudienceDestinationSyncProps> = ({
  selectedAudience,
  selectedTemplate,
  distributionChannelContent,
  onSyncSuccess,
  prevStep,
  onCancel,
}) => {
  const { t } = useTranslation(['ALL', 'destinations']);
  const workspace = useWorkspace();

  const [patchDistributionChannel, { isLoading: isActiveLoading, isSuccess: isActiveSuccess }] = usePatchDistributionChannelMutation();

  const { data: estimateData, isLoading: isEstimateLoading } = useGetEstimateQuery({
    audienceId: distributionChannelContent.distributionChannel.audienceId,
    workspaceId: workspace.id,
  });

  const [advancedSettings] = useState<DistributionChannelAdvancedParameter>({
    activate: true,
    allowAddUsers: true,
    allowRemoveUsers: true,
    allowUpdateUsers: true,
  });

  useEffect(() => {
    if (isActiveSuccess) {
      onSyncSuccess();
    }
  }, [isActiveSuccess]);

  const syncEvent = () => {
    patchDistributionChannel({
      workspaceId: workspace.id,
      audienceId: distributionChannelContent.distributionChannel.audienceId,
      distributionChannelId: distributionChannelContent.distributionChannel.id,
      parameters: advancedSettings,
    });
  };

  return (
    <>
      <div className='flex grow gap-8 p-6'>
        <StepPageHint title='ALL.destinations.steps.sync.finalSummary_title' description='ALL.destinations.steps.sync.finalSummary_description' />
        <div className='flex grow flex-col gap-3'>
          <AudienceTemplateLinkMessage audienceName={selectedAudience.name} templateName={selectedTemplate.Name} />

          <ul className='divide-y divide-quaternary-200 rounded-lg border border-solid border-quaternary-300'>
            <RecapItem
              icon={<TargetIcon className='size-5' />}
              label={
                <Trans
                  i18nKey={'destinations:edit.step.sync.create_segment'}
                  values={{
                    destinationName: distributionChannelContent.distributionChannel.destination,
                    audienceName: distributionChannelContent.distributionChannel.audience.name,
                  }}
                  components={{ b: <b /> } as any}
                />
              }
            />

            <RecapItem icon={<UsersIcon className='size-5' />} label={estimateData?.size + " " + t("ALL:users")} isLoading={isEstimateLoading} />

            <RecapItem
              icon={<DestinationFieldIcon className='size-5' />}
              label={t('destinations:edit.step.sync.properties', { count: distributionChannelContent.integrationsResult.cyclr.action.fields.length })}
            />

            <RecapItem
              icon={<RefeshIcon className='size-5' />}
              label={t(`destinations:edit.step.sync.sync_${distributionChannelContent.distributionChannel.audience.scheduleType.toLowerCase()}` as any)}
            />
          </ul>
        </div>
      </div>

      <StepPageActions
        primaryButton={{
          text: 'destinations:edit.step.complete',
          onClick: syncEvent,
          isLoading: isActiveLoading,
        }}
        secondaryButton={{
          text: 'COMMON.cancel',
          onClick: onCancel,
          isLoading: isActiveLoading,
        }}
        onBackButtonClick={prevStep}
      />
    </>
  );
};
